import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import React, { useState } from "react";
import { useCloneTemplate } from "../../templates/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface DuplicateTemplateMessageProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  templateName: string;
  templateId?: string | number;
  onDuplicate?: () => void; // Changed to no parameters
  actiontype?: string;
  customDescription?: string;
  customInfoItems?: string[];
  newTemplateName?: string; // Add these for controlled component
  setNewTemplateName?: (value: string) => void;
}

const DuplicateTemplateMessage = ({
  open,
  onOpenChange,
  templateName,
  templateId,
  onDuplicate,
  actiontype,
  customDescription,
  customInfoItems,
  newTemplateName: externalNewTemplateName,
  setNewTemplateName: externalSetNewTemplateName,
}: DuplicateTemplateMessageProps) => {
  const cloneTemplateMutation = useCloneTemplate(templateId);
  const [internalNewTemplateName, setInternalNewTemplateName] = useState("");

  // Use external state if provided, otherwise use internal
  const newTemplateName = externalNewTemplateName ?? internalNewTemplateName;
  const setNewTemplateName =
    externalSetNewTemplateName ?? setInternalNewTemplateName;

  const handleCancel = () => {
    setNewTemplateName("");
    onOpenChange(false);
  };

  const handleDuplicate = () => {
    const payload = {
      remote_utility_id: getRemoteUtilityId(),
      template_name: newTemplateName,
    };

    cloneTemplateMutation.mutate(payload);
    setNewTemplateName("");
    onOpenChange(false);
  };

  const defaultInfoItems = [
    "A new template will be created with a copy of all content",
    'The duplicate will have "(Copy)" appended to its name',
    "You can edit the duplicate independently from the original",
  ];

  const infoItems = customInfoItems || defaultInfoItems;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Duplicate Template</DialogTitle>
          <DialogDescription>
            {customDescription ||
              `Are you sure you want to duplicate the template "${templateName}"? A copy will be created with the same content.`}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-gray-700">
            <strong>What happens next:</strong>
          </p>
          <ul className="text-sm text-gray-600 mt-2 space-y-1 list-disc list-inside">
            {infoItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="mt-4 w-full">
          <label className="text-sm font-medium mb-1 block">New Name</label>

          <Input
            className="w-full"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            placeholder="Enter duplicated template name"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          {actiontype === "template" && (
            <Button
              onClick={handleDuplicate}
              disabled={!newTemplateName.trim()}
            >
              Duplicate Template
            </Button>
          )}
          {actiontype === "recipientList" && (
            <Button onClick={onDuplicate} disabled={!newTemplateName.trim()}>
              Duplicate Recipient List
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateTemplateMessage;
